import React, { Dispatch, SetStateAction, useEffect, useState } from 'react'
import {
  Grid,
  Paper,
  Box,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  generateUtilityClasses,
  TableBody,
  Tooltip,
  IconButton,
  CircularProgress,
  Typography,
  MenuItem,
} from '@mui/material'
import { styled } from '@mui/system'
import DeleteIcon from '@mui/icons-material/Delete'
import { visuallyHidden } from '@mui/utils'
import { getComparator, stableSort } from '../../../../../../utils/sorting'
import { storageGet, storageSet } from '../../../../../../utils/storage'
import { AccountBillingData, UspFormViewResult } from '../../../../../../models'
import ConfirmDialog from '../../../../../../components/ConfirmDialog'
import EditIcon from '@mui/icons-material/Edit'
import { useDeleteFormMutation } from '../../../../../../services'
import { enqueueNotification } from '../../../../../../redux/slices/notificationsSlice'
import { useDispatch } from 'react-redux'
import { appRoute } from '../../../../../../constants'
import { useNavigate } from 'react-router-dom'
import { usePopover } from '../../../../../../components/custom-popover'
import Iconify from '../../../../../../components/iconify'
import CustomPopover from '../../../../../../components/custom-popover/custom-popover'
import Label from '../../../../../../components/label'

const classes = generateUtilityClasses('Grid', ['root', 'tableHead', 'activeItem', 'inactiveItem'])

const StyledGrid = styled(Grid)(({ theme }) => ({
  [`&.${classes.root}`]: {
    display: 'flex',
    justifyContent: 'end',
    alignItems: 'center',
    margin: theme.spacing(3, 3, 3, 3),
  },
  [`& .${classes.tableHead}`]: {
    fontWeight: 600,
    background: theme.palette?.background?.main,
  },
  [`& .${classes.activeItem}`]: {
    padding: theme.spacing(0.5, 0, 0, 0),
    width: '84px',
    height: '22px',
    fontSize: '0.8em',
    fontWeight: 500,
    color: '#FFFFFF',
    textAlign: 'center',
    backgroundColor: theme.palette.success.main,
    borderRadius: '4px',
  },
  [`& .${classes.inactiveItem}`]: {
    padding: theme.spacing(0.5, 0, 0, 0),
    width: '84px',
    height: '22px',
    fontSize: '0.8em',
    fontWeight: 500,
    color: '#FFFFFF',
    textAlign: 'center',
    backgroundColor: theme.palette.error.light,
    borderRadius: '4px',
  },
}))

interface Props {
  formsListData: UspFormViewResult[]
  loading: boolean
  setFormsListData: Dispatch<SetStateAction<UspFormViewResult[]>>
}

const FormsTableBlock = ({ loading, setFormsListData, formsListData }: Props) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [deleteForm, { isSuccess: deleteFormIsSuccess, isError }] = useDeleteFormMutation()
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState<{ formId: string } | undefined>(undefined)
  const initialOrder = (storageGet('forms_order') || 'asc') as 'asc' | 'desc'
  const initialOrderBy = (storageGet('forms_order_by') || 'Vendor') as keyof AccountBillingData
  const [order, setOrder] = useState<'asc' | 'desc'>(initialOrder)
  const [orderBy, setOrderBy] = useState<keyof AccountBillingData>(initialOrderBy)
  const [selected, setSelected] = useState<any>(null)
  const popover = usePopover()

  const onRequestSort = (event: React.MouseEvent<unknown>, property: keyof AccountBillingData) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
    storageSet('forms_order', isAsc ? 'desc' : 'asc')
    storageSet('forms_order_by', property)
  }

  const createSortHandler = (property: keyof AccountBillingData) => (event: React.MouseEvent<unknown>) => {
    onRequestSort(event, property)
  }

  const handleDelete = async (): Promise<void> => {
    if (deleteConfirmDialog?.formId) {
      await deleteForm(deleteConfirmDialog)
    }
    setDeleteConfirmDialog(undefined)
  }

  useEffect(() => {
    if (deleteConfirmDialog?.formId) {
      if (deleteFormIsSuccess) {
        const updatedData = formsListData?.map((form) => (form.ID.toString() === deleteConfirmDialog.formId ? { ...form, IsActive: false } : form))
        setFormsListData(updatedData)
        dispatch(enqueueNotification({ message: 'Form removed' }))
      }
    }
  }, [deleteFormIsSuccess])

  const handleEdit = (id: number | string) => {
    if (id) {
      navigate(appRoute.SettingsFormsMapping.replace(':formId', String(id)))
    }
  }

  return (
    <>
      <TableContainer component={Paper} sx={{ overflow: 'auto', maxHeight: 'calc(100vh - 118px)' }} className={classes.root}>
        <Table sx={{ minWidth: 650 }} aria-label='simple table' stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell className={classes.tableHead} sortDirection={orderBy === 'Vendor' ? order : false}>
                <TableSortLabel active={orderBy === 'Vendor'} direction={orderBy === 'Vendor' ? order : 'asc'} onClick={createSortHandler('Vendor')}>
                  State
                  {orderBy === 'Vendor' ? (
                    <Box component='span' sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.tableHead} sortDirection={orderBy === 'VendorCode' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'VendorCode'}
                  direction={orderBy === 'VendorCode' ? order : 'asc'}
                  onClick={createSortHandler('VendorCode')}
                >
                  Description
                  {orderBy === 'VendorCode' ? (
                    <Box component='span' sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.tableHead} sortDirection={orderBy === 'BillNumber' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'BillNumber'}
                  direction={orderBy === 'BillNumber' ? order : 'asc'}
                  onClick={createSortHandler('BillNumber')}
                >
                  Version
                  {orderBy === 'BillNumber' ? (
                    <Box component='span' sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.tableHead} sortDirection={orderBy === 'BillDate' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'BillDate'}
                  direction={orderBy === 'BillDate' ? order : 'asc'}
                  onClick={createSortHandler('BillDate')}
                >
                  Status
                  {orderBy === 'BillDate' ? (
                    <Box component='span' sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>

              <TableCell className={classes.tableHead} align={'right'}>
                Action
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {stableSort(getComparator(order, orderBy), formsListData as any[])?.map((item: any, index: number) => (
              <TableRow key={item.CreditAppID} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                <TableCell>{item?.FormState}</TableCell>
                <TableCell>{item?.Name}</TableCell>
                <TableCell>{item?.Version}</TableCell>
                <TableCell>
                  <Label color={item.IsActive ? 'success' : 'error'}>{item.IsActive ? 'Active' : 'Inactive'}</Label>
                </TableCell>

                <TableCell align={'right'}>
                  <IconButton
                    onClick={(event: React.MouseEvent<HTMLElement>) => {
                      setSelected(item)
                      popover.onOpen(event)
                    }}
                  >
                    <Iconify icon='eva:more-vertical-fill' />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {formsListData?.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} align='center'>
                  No results
                </TableCell>
              </TableRow>
            )}
            {loading && (
              <TableRow>
                <TableCell colSpan={10} align='center'>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <ConfirmDialog
        open={!!deleteConfirmDialog}
        onOk={handleDelete}
        handleClose={() => setDeleteConfirmDialog(undefined)}
        notification={'Form will be removed'}
      />

      <CustomPopover open={popover.open} onClose={popover.onClose} arrow='top-right' sx={{ width: 140 }}>
        <MenuItem
          onClick={(e) => {
            handleEdit(selected?.ID)
            popover.onClose()
          }}
        >
          <Iconify icon='solar:pen-bold' />
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            setDeleteConfirmDialog({ formId: selected?.ID?.toString() as string })

            popover.onClose()
          }}
          sx={{ color: 'error.main' }}
        >
          <Iconify icon='solar:trash-bin-trash-bold' />
          Delete
        </MenuItem>
      </CustomPopover>
    </>
  )
}

export default FormsTableBlock
